import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { finalize } from 'rxjs/operators';

import { usersService } from 'src/app/services/services/users.service';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';

import {
  UsersGlobalProfilesModel,
  TemplateProfile,
  ChildProfile,
  SaveProfileChanges,
  SaveTemplateProfilesResponse,
  CheckForActiveAgentsResponse,
  AffectedProfile
} from '../models';

// Local view-model so we can add UI-only flags like "selected"
interface ProfileTemplateRow extends TemplateProfile {
  selected?: boolean;
}

@Component({
  selector: 'app-profiletemplates',
  templateUrl: './profiletemplates.component.html',
  styleUrls: ['./profiletemplates.component.scss'],
  standalone: false
})
export class ProfileTemplatesComponent implements OnInit, OnDestroy {

  constructor(
    private usersService: usersService,
    private dialog: MatDialog,
    private i18n: I18NextPipe
  ) { }

  BreadcrumbsLink = [
    { label: 'Home', url: '/' },
    { label: 'Profile Templates', url: '/users/profile-templates' }
  ];

  isLoading = false;
  isSaving = false;

  errorMessage = '';
  successMessage = '';

  // data
  model: UsersGlobalProfilesModel | null = null;
  profiles: ProfileTemplateRow[] = [];
  originalProfiles: TemplateProfile[] = [];

  // unified search: “Filter name or skills…”
  filterText = '';

  // change tracking
  hasChanges = false;

  // dialogs
  childProfiles: ChildProfile[] = [];
  affectedProfiles: AffectedProfile[] = [];
  activeAgentsCount = 0;

  @ViewChild('childProfilesDialog') childProfilesDialog!: TemplateRef<any>;
  @ViewChild('changeWarningDialog') changeWarningDialog!: TemplateRef<any>;
  @ViewChild('activeAgentsDialog') activeAgentsDialog!: TemplateRef<any>;

  // last diff used when confirming save in warning dialog
  private lastAddedProfiles: TemplateProfile[] = [];
  private lastEditedProfiles: TemplateProfile[] = [];
  private lastDeletedProfiles: string[] = [];

  async ngOnInit(): Promise<void> {
    this.loadProfiles();
  }

  ngOnDestroy(): void { }

  // --- HELP DIALOG ---
  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18n.transform('users:help_title_globalprofiles', { defaultValue: 'Global Profiles help' }),
        text: this.i18n.transform('users:help_globalprofiles', { defaultValue: '' }),
        renderAsHtml: true
      }
    });
  }

  // --- LOAD DATA ---
  private loadProfiles(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.usersService.GetProfileTemplates()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: UsersGlobalProfilesModel) => {
          this.model = res;
          this.profiles = (res.profiles || []).map(p => ({ ...p, selected: false }));
          // deep clone to keep pristine copy (no UI flags)
          this.originalProfiles = JSON.parse(JSON.stringify(res.profiles || []));
          this.hasChanges = false;
        },
        error: () => {
          this.errorMessage = this.i18n.transform('common:generic_error', { defaultValue: 'Error loading profile templates.' });
        }
      });
  }

  // --- FILTERING (Filter name OR skills) ---
  get filteredProfiles(): ProfileTemplateRow[] {
    if (!this.filterText) {
      return this.profiles;
    }

    const f = this.filterText.toLowerCase();

    return this.profiles.filter(p => {
      const nameMatch = (p.name || '').toLowerCase().includes(f);

      const skillsMatch = p.skills?.some(s =>
        s.qualities?.some(q => (q.name || '').toLowerCase().includes(f))
      );

      return nameMatch || skillsMatch;
    });
  }

  onFilterChange(value: string): void {
    this.filterText = value;
  }

  // --- PROFILE EDITING ---

  addProfile(): void {
    const nextNumber =
      (this.profiles.length > 0
        ? Math.max(...this.profiles.map(p => p.number || 0)) + 1
        : 1);

    const newProfile: ProfileTemplateRow = {
      enableForChildren: 0, // CopyLevel.NONE
      groupProfile: null,
      number: nextNumber,
      id: null,
      templateProfileId: null,
      name: '',
      skills: [],
      hasChildProfile: false,
      selected: false
    };

    this.profiles.push(newProfile);
    this.updateHasChanges();
  }

  deleteProfile(profile: ProfileTemplateRow): void {
    this.profiles = this.profiles.filter(p => p !== profile);
    this.updateHasChanges();
  }

  onProfileNameChange(profile: ProfileTemplateRow): void {
    this.updateHasChanges();
  }

  // checkbox for “Copy selected profiles”
  toggleProfileSelected(profile: ProfileTemplateRow, checked: boolean): void {
    profile.selected = checked;
  }

  get canCopySelected(): boolean {
    return this.profiles.some(p => p.selected);
  }

  copySelectedProfiles(): void {
    const selected = this.profiles.filter(p => p.selected);
    if (!selected.length) return;

    const maxNumber = this.profiles.length > 0
      ? Math.max(...this.profiles.map(p => p.number || 0))
      : 0;

    let currentNumber = maxNumber;
    selected.forEach(src => {
      currentNumber++;
      const copy: ProfileTemplateRow = {
        ...JSON.parse(JSON.stringify(src)), // deep copy skills
        id: null,
        templateProfileId: src.templateProfileId,
        number: currentNumber,
        name: (src.name || '') + ' - copy',
        selected: false,
        hasChildProfile: false
      };
      this.profiles.push(copy);
    });

    this.updateHasChanges();
  }

  // --- CHILD PROFILES (eye icon) ---

  openChildProfiles(profile: ProfileTemplateRow, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!profile.id) return;

    this.isLoading = true;
    this.usersService.GetChildProfiles(profile.id)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: ChildProfile[]) => {
          this.childProfiles = res || [];
          this.dialog.open(this.childProfilesDialog, {
            width: '600px',
            data: { profile }
          });
        },
        error: () => {
          this.errorMessage = this.i18n.transform('common:generic_error', { defaultValue: 'Error loading child profiles.' });
        }
      });
  }

  // --- SKILLS UI (chips) ---

  onSkillLevelChange(): void {
    this.updateHasChanges();
  }

  removeSkill(profile: ProfileTemplateRow, serviceId: number, qualityId: number): void {
    if (!profile.skills) return;
    profile.skills = profile.skills.map(s => ({
      ...s,
      qualities: s.qualities?.filter(q => q.qualityId !== qualityId) || []
    })).filter(s => (s.qualities?.length || 0) > 0);

    this.updateHasChanges();
  }

  openAddSkillsDialog(profile: ProfileTemplateRow, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    // TODO: implement full skills picker dialog.
    // For now, just a hook.
  }

  // --- CHANGE TRACKING / DIFF ---

  private updateHasChanges(): void {
    const originalById = new Map<number, TemplateProfile>();
    this.originalProfiles.forEach(p => {
      if (p.id != null) {
        originalById.set(p.id, p);
      }
    });

    const added = this.profiles.filter(p => p.id == null);
    const deleted = this.originalProfiles.filter(
      op => op.id != null && !this.profiles.some(p => p.id === op.id)
    );
    const edited = this.profiles.filter(p => {
      if (p.id == null) return false;
      const original = originalById.get(p.id);
      if (!original) return false;

      return JSON.stringify({
        name: original.name,
        number: original.number,
        enableForChildren: original.enableForChildren,
        skills: original.skills
      }) !== JSON.stringify({
        name: p.name,
        number: p.number,
        enableForChildren: p.enableForChildren,
        skills: p.skills
      });
    });

    this.hasChanges = added.length > 0 || deleted.length > 0 || edited.length > 0;
  }

  private buildDiff(): {
    addedProfiles: TemplateProfile[];
    editedProfiles: TemplateProfile[];
    deletedProfiles: string[];
  } {
    const originalById = new Map<number, TemplateProfile>();
    this.originalProfiles.forEach(p => {
      if (p.id != null) {
        originalById.set(p.id, p);
      }
    });

    const addedProfiles: TemplateProfile[] = this.profiles
      .filter(p => p.id == null)
      .map(p => {
        const { selected, ...rest } = p;
        return rest;
      });

    const deletedProfiles = this.originalProfiles
      .filter(op => op.id != null && !this.profiles.some(p => p.id === op.id))
      .map(op => String(op.id));

    const editedProfiles: TemplateProfile[] = this.profiles
      .filter(p => {
        if (p.id == null) return false;
        const original = originalById.get(p.id);
        if (!original) return false;

        return JSON.stringify({
          name: original.name,
          number: original.number,
          enableForChildren: original.enableForChildren,
          skills: original.skills
        }) !== JSON.stringify({
          name: p.name,
          number: p.number,
          enableForChildren: p.enableForChildren,
          skills: p.skills
        });
      })
      .map(p => {
        const { selected, ...rest } = p;
        return rest;
      });

    return { addedProfiles, editedProfiles, deletedProfiles };
  }

  undoChanges(): void {
    if (!this.hasChanges) return;

    this.profiles = (JSON.parse(JSON.stringify(this.originalProfiles)) as TemplateProfile[])
      .map(p => ({ ...p, selected: false }));
    this.hasChanges = false;
    this.errorMessage = '';
    this.successMessage = '';
  }

  // --- SAVE FLOW (using buildDiff) ---

  saveProfiles(): void {
    this.errorMessage = '';
    this.successMessage = '';

    const { addedProfiles, editedProfiles, deletedProfiles } = this.buildDiff();

    if (!addedProfiles.length && !editedProfiles.length && !deletedProfiles.length) return;

    const skillsAdd = addedProfiles.reduce(
      (sum, p) => sum + (p.skills?.reduce((s, sk) => s + (sk.qualities?.length || 0), 0) || 0),
      0
    );
    const skillsEdit = editedProfiles.reduce(
      (sum, p) => sum + (p.skills?.reduce((s, sk) => s + (sk.qualities?.length || 0), 0) || 0),
      0
    );

    if (addedProfiles.length > 0 && skillsAdd === 0) {
      this.errorMessage = this.i18n.transform('users:needoneskill', { defaultValue: 'At least one skill is required for new profiles.' });
      return;
    }

    if (editedProfiles.length > 0 && skillsEdit === 0) {
      this.errorMessage = this.i18n.transform('users:needoneskill', { defaultValue: 'At least one skill is required when editing profiles.' });
      return;
    }

    this.lastAddedProfiles = addedProfiles;
    this.lastEditedProfiles = editedProfiles;
    this.lastDeletedProfiles = deletedProfiles;

    const checkPayload: SaveProfileChanges = {
      changeActiveAgents: false,
      isExcute: false,
      addedProfiles,
      editedProfiles,
      deletedProfiles
    };

    this.isSaving = true;

    this.usersService.CheckForActiveAgents(checkPayload)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: (res: CheckForActiveAgentsResponse) => {
          if (res.isError) {
            this.errorMessage = this.i18n.transform('common:generic_error', { defaultValue: 'Error checking active agents.' });
            return;
          }

          if (!res.haveSkillsBeenChanged || res.numberOfActiveAgents <= 0) {
            this.callSaveTemplateProfiles(false);
          } else {
            this.activeAgentsCount = res.numberOfActiveAgents;
            const ref = this.dialog.open(this.activeAgentsDialog, { width: '500px' });
            ref.afterClosed().subscribe(result => {
              if (result === 'confirm') {
                this.callSaveTemplateProfiles(true);
              }
            });
          }
        },
        error: () => {
          this.errorMessage = this.i18n.transform('common:generic_error', { defaultValue: 'Error checking active agents.' });
        }
      });
  }

  private callSaveTemplateProfiles(changeActiveAgents: boolean): void {
    const payload: SaveProfileChanges = {
      changeActiveAgents,
      isExcute: false,
      addedProfiles: this.lastAddedProfiles,
      editedProfiles: this.lastEditedProfiles,
      deletedProfiles: this.lastDeletedProfiles
    };

    this.isSaving = true;
    this.usersService.SaveTemplateProfiles(payload)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: (res: SaveTemplateProfilesResponse) => {
          if (res.error) {
            this.errorMessage = res.message || this.i18n.transform('common:generic_error', { defaultValue: 'Error saving profiles.' });
            return;
          }

          if (res.returnObject && res.returnObject.length > 0) {
            this.affectedProfiles = res.returnObject;
            const ref = this.dialog.open(this.changeWarningDialog, { width: '600px' });
            ref.afterClosed().subscribe(result => {
              if (result === 'confirm') {
                this.executeSaveTemplateProfiles(changeActiveAgents);
              }
            });
          } else {
            this.executeSaveTemplateProfiles(changeActiveAgents);
          }
        },
        error: () => {
          this.errorMessage = this.i18n.transform('common:generic_error', { defaultValue: 'Error saving profiles.' });
        }
      });
  }

  private executeSaveTemplateProfiles(changeActiveAgents: boolean): void {
    const payload: SaveProfileChanges = {
      changeActiveAgents,
      isExcute: true,
      addedProfiles: this.lastAddedProfiles,
      editedProfiles: this.lastEditedProfiles,
      deletedProfiles: this.lastDeletedProfiles
    };

    this.isSaving = true;
    this.usersService.SaveTemplateProfiles(payload)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: (res: SaveTemplateProfilesResponse) => {
          if (res.error) {
            this.errorMessage = res.message || this.i18n.transform('common:generic_error', { defaultValue: 'Error saving profiles.' });
            return;
          }
          this.successMessage = this.i18n.transform('users:profiles_saved', { defaultValue: 'Profiles saved successfully.' });
          this.loadProfiles();
        },
        error: () => {
          this.errorMessage = this.i18n.transform('common:generic_error', { defaultValue: 'Error saving profiles.' });
        }
      });
  }
}
